import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './TruckCompanyTrucks.scss';
import apiClient from '../../../../../Config/apiClient';
import { ClipLoader } from 'react-spinners';
import { showErrorToast } from '../../../../../Context/Toaster/Toaster';
import backIcon from '../../../../../Assets/images/back-icon.svg';
import moment from 'moment';
import { DATE_FORMAT } from '../../Utils/Constants';
import ViewTruckModal from '../../../../TC/src/Components/TrucksItems/ViewTruckModal/ViewTruckModal';

const truckPath = process.env.REACT_APP_HS_TRUCK_BASE_PATH;

const TruckCompanyTrucks = () => {
    const { companyId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const companyName = searchParams.get('name') || 'Company';
    
    const [trucks, setTrucks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewTruck, setViewTruck] = useState(null);
    const [trucksPerPage, setTrucksPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const totalTrucks = trucks.length;
    const totalPages = Math.ceil(totalTrucks / trucksPerPage);
    const startIndex = (currentPage - 1) * trucksPerPage;

    const currentTrucks = trucks.slice(startIndex, startIndex + trucksPerPage);

    const fetchTrucks = React.useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiClient.post(`${truckPath}/company/${companyId}/trucks`, {});
            if (response.data.status === 'SUCCESS') {
                setTrucks(response.data.data);
            }
        } catch (error) {
            showErrorToast('Failed to fetch trucks');
            console.error('Error fetching trucks:', error);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        if (companyId) {
            fetchTrucks();
        }
    }, [companyId, fetchTrucks]);

    const handleViewTruck = (truckId) => {
        const truck = trucks.find(t => t.truck_id === truckId);
        if (truck) {
            setViewTruck(truck);
        }
    };

    const getDocumentStatus = (truck) => {
        const docs = [truck.cvor_doc, truck.permit_doc, truck.insurance_doc];
        const uploadedDocs = docs.filter(doc => doc).length;
        
        if (uploadedDocs === 3) {
            const now = new Date();
            const expiries = [
                truck.cvor_expiry,
                truck.permit_expiry,
                truck.insurance_expiry,
                truck.emission_expiry
            ].filter(exp => exp);
            
            const hasExpired = expiries.some(exp => new Date(exp) < now);
            if (hasExpired) return { status: 'expired', count: '3/3 Docs', color: '#dc3545' };
            return { status: 'complete', count: '3/3 Docs', color: '#28a745' };
        } else if (uploadedDocs > 0) {
            return { status: 'partial', count: `${uploadedDocs}/3 Docs`, color: '#ffc107' };
        }
        return { status: 'missing', count: '0/3 Docs', color: '#6c757d' };
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleBack = () => {
        navigate('/hs/t-companies');
    };

    return (
        <main className="full-wraper">
            <div className="main-wraper">
                <div className="container-fluid">
                    <div className="main-inner">
                        <div className="truck-company-trucks-page">
                            {/* Header */}
                            <div className="page-header">
                                <button className="back-button" onClick={handleBack}>
                                    <img src={backIcon} alt="Back" />
                                    Back to T-Companies
                                </button>
                                <h1 className="page-title">Trucks & Documents - {companyName}</h1>
                            </div>

                            {/* Content */}
                            {loading ? (
                                <div className="loading-container">
                                    <ClipLoader size={50} color="grey" />
                                </div>
                            ) : (
                                <div className="table-cvr">
                                    <div className="table-inner">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Truck ID</th>
                                                    <th>Vehicle No</th>
                                                    <th>License Plate No</th>
                                                    <th>WSIB Insurance No.</th>
                                                    <th>Truck Type</th>
                                                    <th>Documents</th>
                                                    <th>Created</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentTrucks.length > 0 ? (
                                                    currentTrucks.map((truck, i) => {
                                                        const docStatus = getDocumentStatus(truck);
                                                        return (
                                                            <tr key={truck.truck_id}>
                                                                <td>{startIndex + i + 1}</td>
                                                                <td>
                                                                    <a 
                                                                        className="vu-link" 
                                                                        style={{ cursor: "pointer", color: "#59a52c", textDecoration: "underline" }} 
                                                                        onClick={() => handleViewTruck(truck.truck_id)}
                                                                    >
                                                                        T-{truck.truck_id}
                                                                    </a>
                                                                </td>
                                                                <td>{truck.vehicle_no || '-'}</td>
                                                                <td>{truck.license_plate_no || '-'}</td>
                                                                <td>{truck.insurance_no || '-'}</td>
                                                                <td>{truck.truck_type || '-'}</td>
                                                                <td>
                                                                    <span 
                                                                        className="doc-status-badge"
                                                                        style={{ 
                                                                            backgroundColor: docStatus.color,
                                                                            color: 'white',
                                                                            padding: '4px 8px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '12px',
                                                                            fontWeight: '500'
                                                                        }}
                                                                    >
                                                                        {docStatus.count}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {truck.last_upated_datetime
                                                                        ? moment(truck.last_upated_datetime).format(DATE_FORMAT)
                                                                        : "-"}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4 text-muted">
                                                            No Trucks Found.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {trucks.length > 0 && (
                                        <div className="dt-layout-row d-flex align-items-center justify-content-between mt-3">
                                            <div className="dt-length">
                                                Showing{" "}
                                                <select 
                                                    className="dt-input" 
                                                    value={trucksPerPage} 
                                                    onChange={(e) => { 
                                                        setCurrentPage(1); 
                                                        setTrucksPerPage(Number(e.target.value)); 
                                                    }} 
                                                >
                                                    <option value="5">5</option>
                                                    <option value="10">10</option>
                                                    <option value="50">50</option>
                                                    <option value="100">100</option>
                                                </select>{" "}
                                                of {totalTrucks} entries
                                            </div>

                                            <div className="dt-paging d-flex align-items-center" style={{ gap: "5px" }}>
                                                <button 
                                                    className="dt-paging-button previous" 
                                                    disabled={currentPage === 1} 
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                >
                                                    Previous
                                                </button>
                                                {Array.from({ length: totalPages }).map((_, i) => (
                                                    <button 
                                                        key={i + 1} 
                                                        className={`dt-paging-button ${currentPage === i + 1 ? "current" : ""}`} 
                                                        onClick={() => handlePageChange(i + 1)}
                                                    >
                                                        {i + 1}
                                                    </button>
                                                ))}
                                                <button 
                                                    className="dt-paging-button next" 
                                                    disabled={currentPage === totalPages} 
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* View Truck Detail Modal */}
            {viewTruck && (
                <ViewTruckModal
                    truck={viewTruck}
                    onClose={() => setViewTruck(null)}
                />
            )}
        </main>
    );
};

export default TruckCompanyTrucks;
